/**
 * Administração → Integração SENATRAN: o cadastro que o portal pede e a carga histórica
 * (ADR-035).
 *
 * Antes do credenciamento, a concessionária preenche no portal da SENATRAN a concessão, os
 * pórticos e os operadores de pagamento. Esta tela guarda o mesmo cadastro aqui e diz,
 * campo por campo, **o que falta** — as regras de forma são do domínio, no servidor. É
 * também onde estão a URL e a chave do webhook, que se colam no portal, e o arquivo da
 * carga histórica, com prévia antes de baixar.
 *
 * Três travas que a tela mostra em vez de esconder: o CNPJ da concessão não muda depois de
 * um evento oficial; o CNPJ do operador não muda nunca (desativa-se); e gerar chave nova
 * derruba a antiga na hora.
 */
import { useCallback, useEffect, useMemo, useState } from "react";

import { ErroDaApi, api } from "../api/cliente";
import type {
  CadastroDaConcessao,
  OperadorDoCadastro,
  PorticoDoCadastro,
  PreviaDaCarga,
} from "../api/tipos";
import { Badge, Botao, Cartao, Estado, Painel } from "../componentes/base";
import { Icone } from "../componentes/icones";
import { numeroLongo } from "../formato";
import { MEIOS, TIPOS, lerIds, totalDePendencias } from "./cadastroSenatran";
import { agoraDoInquilino } from "../relogio";

const BASE = "/tollvision/senatran";

const ENTRADA =
  "min-h-8 w-full rounded-botao border border-linha bg-painel px-2 text-[11.5px] text-tinta";

function mensagem(falha: unknown): string {
  return falha instanceof ErroDaApi ? falha.message : "Falha inesperada.";
}

export function IntegracaoSenatran() {
  const [cadastros, definirCadastros] = useState<CadastroDaConcessao[] | null>(
    null,
  );
  const [erro, definirErro] = useState<ErroDaApi | null>(null);
  const [escolhida, definirEscolhida] = useState<string | null>(null);

  const recarregar = useCallback(async () => {
    try {
      definirCadastros(await api<CadastroDaConcessao[]>(`${BASE}/cadastro`));
      definirErro(null);
    } catch (falha) {
      definirErro(
        falha instanceof ErroDaApi
          ? falha
          : new ErroDaApi("Falha inesperada.", 0),
      );
    }
  }, []);

  useEffect(() => {
    void recarregar();
  }, [recarregar]);

  if (erro) {
    return (
      <Painel titulo="O cadastro que o portal pede">
        <Estado
          tipo={
            erro.semLicenca
              ? "sem-licenca"
              : erro.status === 403
                ? "bloqueado"
                : "erro"
          }
          titulo={
            erro.semLicenca
              ? "Esta organização não licenciou o módulo TollVision."
              : erro.status === 403
                ? "Esta área é do administrador da organização."
                : "Não foi possível carregar o cadastro."
          }
          detalhe={erro.message}
        />
      </Painel>
    );
  }
  if (!cadastros) {
    return (
      <Painel titulo="O cadastro que o portal pede">
        <Estado tipo="carregando" titulo="Carregando o cadastro…" />
      </Painel>
    );
  }
  if (cadastros.length === 0) {
    return (
      <Painel titulo="O cadastro que o portal pede">
        <Estado tipo="vazio" titulo="Nenhuma concessão cadastrada." />
      </Painel>
    );
  }

  const atual =
    cadastros.find((c) => c.concessao_id === escolhida) ?? cadastros[0]!;

  return (
    <>
      <Painel
        titulo="O cadastro que o portal pede"
        rodape={
          <span>
            O mesmo cadastro vai ao portal da SENATRAN no credenciamento. Aqui
            ele fica guardado e conferido: o que falta aparece antes, não depois
            de uma recusa.
          </span>
        }
      >
        {cadastros.length > 1 && (
          <div
            className="flex flex-wrap gap-1"
            role="tablist"
            aria-label="Concessões"
          >
            {cadastros.map((c) => {
              const faltam = totalDePendencias(c);
              return (
                <button
                  key={c.concessao_id}
                  type="button"
                  role="tab"
                  aria-selected={c.concessao_id === atual.concessao_id}
                  onClick={() => definirEscolhida(c.concessao_id)}
                  className={`inline-flex min-h-8 items-center gap-1.5 rounded-botao border px-2.5 text-[11.5px] font-semibold ${
                    c.concessao_id === atual.concessao_id
                      ? "border-azul-cheio bg-azul-cheio text-white"
                      : "border-linha bg-painel-2 text-tinta-2 hover:bg-realce"
                  }`}
                >
                  {c.concessao}
                  <Badge cor={faltam ? "amarelo" : "verde"}>
                    {faltam ? `${faltam} pendência(s)` : "completo"}
                  </Badge>
                </button>
              );
            })}
          </div>
        )}

        <Pendencias cadastro={atual} />

        <div className="grid gap-2.5 [grid-template-columns:repeat(auto-fit,minmax(300px,1fr))]">
          <FormularioDaConcessao
            key={`${atual.concessao_id}-${atual.atualizado_em ?? ""}`}
            cadastro={atual}
            aoGravar={recarregar}
          />
          <Webhook cadastro={atual} aoGirar={recarregar} />
        </div>

        <Porticos cadastro={atual} aoGravar={recarregar} />
        <Operadores cadastro={atual} aoGravar={recarregar} />
      </Painel>

      <CargaHistorica cadastro={atual} />
    </>
  );
}

// --------------------------------------------------------------------------- pendências

function Pendencias({ cadastro }: { cadastro: CadastroDaConcessao }) {
  const itens = [
    ...cadastro.pendencias.map((p) => `Concessão: ${p}`),
    ...cadastro.porticos
      .filter((p) => p.ativo)
      .flatMap((p) => p.pendencias.map((f) => `${p.nome}: ${f}`)),
    ...cadastro.operadores
      .filter((o) => o.ativo)
      .flatMap((o) => o.pendencias.map((f) => `${o.razao_social}: ${f}`)),
  ];
  if (itens.length === 0) {
    return (
      <p className="flex items-center gap-1.5 rounded-cartao border border-verde-borda bg-verde-fundo px-3 py-2 text-[11.5px] text-verde">
        <Icone.Certo />
        Cadastro completo para o portal.
      </p>
    );
  }
  return (
    <div
      className="rounded-cartao border border-amarelo-borda bg-amarelo-fundo px-3 py-2"
      aria-label="O que falta"
    >
      <p className="mb-1 flex items-center gap-1.5 text-[11.5px] font-semibold text-amarelo">
        <Icone.Alerta />
        Falta para o portal ({itens.length})
      </p>
      <ul className="grid gap-0.5 text-[11px] text-tinta-2 [grid-template-columns:repeat(auto-fit,minmax(240px,1fr))]">
        {itens.map((i) => (
          <li key={i}>· {i}</li>
        ))}
      </ul>
    </div>
  );
}

// --------------------------------------------------------------------------- concessão

function CampoDeTexto({
  rotulo,
  valor,
  aoMudar,
  placeholder,
  desabilitado,
  titulo,
}: {
  rotulo: string;
  valor: string;
  aoMudar: (v: string) => void;
  placeholder?: string | undefined;
  desabilitado?: boolean | undefined;
  titulo?: string | undefined;
}) {
  return (
    <label className="grid gap-1 text-[11px] text-tinta-3" title={titulo}>
      {rotulo}
      <input
        value={valor}
        aria-label={rotulo}
        placeholder={placeholder}
        disabled={desabilitado}
        onChange={(e) => aoMudar(e.target.value)}
        className={`${ENTRADA} disabled:opacity-60`}
      />
    </label>
  );
}

function FormularioDaConcessao({
  cadastro,
  aoGravar,
}: {
  cadastro: CadastroDaConcessao;
  aoGravar: () => Promise<void>;
}) {
  const [dados, definirDados] = useState({
    cnpj: cadastro.cnpj ?? "",
    nome_concessao: cadastro.nome_concessao ?? "",
    url: cadastro.url ?? "",
    url_pagamento: cadastro.url_pagamento ?? "",
    url_contestacao: cadastro.url_contestacao ?? "",
    email_falha_webhook: cadastro.email_falha_webhook ?? "",
  });
  const [aviso, definirAviso] = useState<{ texto: string; ok: boolean } | null>(
    null,
  );
  const [gravando, definirGravando] = useState(false);
  const mudar = (chave: keyof typeof dados) => (v: string) =>
    definirDados((d) => ({ ...d, [chave]: v }));

  async function gravar() {
    definirGravando(true);
    try {
      await api(`${BASE}/cadastro/${cadastro.concessao_id}`, {
        method: "PUT",
        corpo: dados,
      });
      definirAviso({ texto: "Cadastro gravado.", ok: true });
      await aoGravar();
    } catch (falha) {
      definirAviso({ texto: mensagem(falha), ok: false });
    } finally {
      definirGravando(false);
    }
  }

  return (
    <Cartao titulo="Concessão" icone={<Icone.Lista />}>
      <div className="grid gap-2">
        <CampoDeTexto
          rotulo="CNPJ da concessão"
          valor={dados.cnpj}
          aoMudar={mudar("cnpj")}
          placeholder="00.000.000/0000-00"
          desabilitado={cadastro.cnpj_travado}
          titulo={
            cadastro.cnpj_travado
              ? "Um evento oficial já saiu com este CNPJ: ele não muda mais."
              : undefined
          }
        />
        {cadastro.cnpj && !cadastro.cnpj_valido && (
          <p className="text-[10.5px] text-amarelo">
            Este CNPJ não fecha o dígito verificador — é o fictício da
            demonstração. Troque pelo CNPJ real antes do credenciamento.
          </p>
        )}
        <CampoDeTexto
          rotulo="Nome público da concessão"
          valor={dados.nome_concessao}
          aoMudar={mudar("nome_concessao")}
        />
        <CampoDeTexto
          rotulo="URL da concessionária"
          valor={dados.url}
          aoMudar={mudar("url")}
          placeholder="https://"
        />
        <CampoDeTexto
          rotulo="URL de pagamento"
          valor={dados.url_pagamento}
          aoMudar={mudar("url_pagamento")}
          placeholder="https://"
        />
        <CampoDeTexto
          rotulo="URL de contestação"
          valor={dados.url_contestacao}
          aoMudar={mudar("url_contestacao")}
          placeholder="https://"
        />
        <CampoDeTexto
          rotulo="E-mail para falha do webhook (opcional)"
          valor={dados.email_falha_webhook}
          aoMudar={mudar("email_falha_webhook")}
        />
        <div className="flex flex-wrap items-center gap-2">
          <Botao
            papel="primaria"
            disabled={gravando || !dados.cnpj.trim()}
            onClick={() => void gravar()}
          >
            Gravar concessão
          </Botao>
          {aviso && (
            <span
              className={`text-[11px] ${aviso.ok ? "text-verde" : "text-vermelho"}`}
            >
              {aviso.texto}
            </span>
          )}
        </div>
      </div>
    </Cartao>
  );
}

// --------------------------------------------------------------------------- webhook

async function copiar(texto: string) {
  try {
    await navigator.clipboard.writeText(texto);
  } catch {
    // Sem permissão de área de transferência: o texto continua selecionável na tela.
  }
}

function Webhook({
  cadastro,
  aoGirar,
}: {
  cadastro: CadastroDaConcessao;
  aoGirar: () => Promise<void>;
}) {
  const [mostrar, definirMostrar] = useState(false);
  const [aviso, definirAviso] = useState<string | null>(null);
  const interna = cadastro.url_webhook.startsWith("http://");

  async function girar() {
    if (
      !window.confirm(
        "A chave atual para de valer agora. A nova precisa ser colada no portal da SENATRAN em seguida. Gerar chave nova?",
      )
    )
      return;
    try {
      await api(`${BASE}/cadastro/${cadastro.concessao_id}/girar-chave`, {
        method: "POST",
      });
      definirAviso("Chave nova gerada. Cole no portal.");
      definirMostrar(true);
      await aoGirar();
    } catch (falha) {
      definirAviso(mensagem(falha));
    }
  }

  return (
    <Cartao titulo="Retorno (webhook)" icone={<Icone.Recarga />}>
      <div className="grid gap-2 text-[11.5px]">
        <div>
          <p className="text-[11px] text-tinta-3">URL do webhook</p>
          <p className="mono rounded-botao border border-linha bg-painel px-2 py-1.5 text-[11px] wrap-anywhere text-tinta">
            {cadastro.url_webhook}
          </p>
          {interna && (
            <p className="mt-1 text-[10.5px] text-amarelo">
              Endereço interno: só o emulador o alcança. Com o serviço oficial,
              a base precisa ser a URL pública da API
              (COCKPIT_SENATRAN_WEBHOOK_BASE).
            </p>
          )}
        </div>
        <div>
          <p className="text-[11px] text-tinta-3">
            Chave (cabeçalho Authorization) · versão{" "}
            {cadastro.versao_chave_webhook}
          </p>
          {cadastro.chave_webhook ? (
            <p className="mono rounded-botao border border-linha bg-painel px-2 py-1.5 text-[11px] text-tinta">
              {mostrar ? cadastro.chave_webhook : "••••••••••••••"}
            </p>
          ) : (
            <p className="text-[11px] text-tinta-3">
              Cadastre o CNPJ para gerar a chave.
            </p>
          )}
        </div>
        <div className="flex flex-wrap gap-1.5">
          <Botao onClick={() => void copiar(cadastro.url_webhook)}>
            Copiar URL
          </Botao>
          {cadastro.chave_webhook && (
            <>
              <Botao onClick={() => definirMostrar((m) => !m)}>
                {mostrar ? "Ocultar chave" : "Mostrar chave"}
              </Botao>
              <Botao onClick={() => void copiar(cadastro.chave_webhook ?? "")}>
                Copiar chave
              </Botao>
              <Botao onClick={() => void girar()}>
                <Icone.Chave />
                Gerar chave nova
              </Botao>
            </>
          )}
        </div>
        {aviso && <p className="text-[11px] text-tinta-2">{aviso}</p>}
      </div>
    </Cartao>
  );
}

// --------------------------------------------------------------------------- pórticos

function Porticos({
  cadastro,
  aoGravar,
}: {
  cadastro: CadastroDaConcessao;
  aoGravar: () => Promise<void>;
}) {
  const [editando, definirEditando] = useState<string | null>(null);
  return (
    <Cartao
      titulo={`Pórticos (${cadastro.porticos.length})`}
      icone={<Icone.Alvo />}
    >
      <ul aria-label="Pórticos" className="grid gap-1.5">
        {cadastro.porticos.map((p) => (
          <li
            key={p.praca_id}
            className="rounded-[7px] border border-linha bg-painel-2 px-2.5 py-2"
          >
            <div className="flex flex-wrap items-center gap-2">
              <span className="min-w-0 flex-1">
                <b className="text-tinta">{p.nome}</b>
                <small className="block text-[11px] text-tinta-3">
                  {p.localizacao ??
                    [p.rodovia, p.km != null ? `km ${p.km}` : null]
                      .filter(Boolean)
                      .join(" · ")}
                  {p.municipio_tom ? ` · TOM ${p.municipio_tom}` : ""}
                </small>
              </span>
              {!p.ativo && <Badge cor="cinza">inativo</Badge>}
              <Badge cor={p.pendencias.length ? "amarelo" : "verde"}>
                {p.pendencias.length
                  ? `${p.pendencias.length} pendência(s)`
                  : "completo"}
              </Badge>
              <Botao
                onClick={() =>
                  definirEditando((e) => (e === p.praca_id ? null : p.praca_id))
                }
              >
                <Icone.Lapis />
                {editando === p.praca_id ? "Fechar" : "Editar"}
              </Botao>
            </div>
            {editando === p.praca_id && (
              <FormularioDoPortico
                concessaoId={cadastro.concessao_id}
                portico={p}
                aoGravar={async () => {
                  definirEditando(null);
                  await aoGravar();
                }}
              />
            )}
          </li>
        ))}
      </ul>
    </Cartao>
  );
}

function FormularioDoPortico({
  concessaoId,
  portico,
  aoGravar,
}: {
  concessaoId: string;
  portico: PorticoDoCadastro;
  aoGravar: () => Promise<void>;
}) {
  const [dados, definirDados] = useState({
    municipio_tom: portico.municipio_tom?.toString() ?? "",
    localizacao: portico.localizacao ?? "",
    latitude: portico.latitude ?? "",
    longitude: portico.longitude ?? "",
    vao_altura: portico.vao_altura?.toString() ?? "",
    vao_largura: portico.vao_largura?.toString() ?? "",
  });
  const [ativo, definirAtivo] = useState(portico.ativo);
  const [erro, definirErro] = useState<string | null>(null);
  const mudar = (chave: keyof typeof dados) => (v: string) =>
    definirDados((d) => ({ ...d, [chave]: v }));
  const numero = (v: string) => (v.trim() ? Number(v.replace(",", ".")) : null);

  async function gravar() {
    try {
      await api(
        `${BASE}/cadastro/${concessaoId}/porticos/${portico.praca_id}`,
        {
          method: "PUT",
          corpo: {
            municipio_tom: numero(dados.municipio_tom),
            localizacao: dados.localizacao,
            latitude: dados.latitude,
            longitude: dados.longitude,
            vao_altura: numero(dados.vao_altura),
            vao_largura: numero(dados.vao_largura),
            ativo,
          },
        },
      );
      await aoGravar();
    } catch (falha) {
      definirErro(mensagem(falha));
    }
  }

  return (
    <div className="mt-2 grid gap-2 [grid-template-columns:repeat(auto-fit,minmax(150px,1fr))]">
      <CampoDeTexto
        rotulo="Município (código TOM)"
        valor={dados.municipio_tom}
        aoMudar={mudar("municipio_tom")}
      />
      <CampoDeTexto
        rotulo="Localização"
        valor={dados.localizacao}
        aoMudar={mudar("localizacao")}
        placeholder="BR 262 Km 12 Leste"
      />
      <CampoDeTexto
        rotulo="Latitude"
        valor={dados.latitude}
        aoMudar={mudar("latitude")}
        placeholder="-20,751100"
      />
      <CampoDeTexto
        rotulo="Longitude"
        valor={dados.longitude}
        aoMudar={mudar("longitude")}
        placeholder="-51,678300"
      />
      <CampoDeTexto
        rotulo="Vão: altura (m)"
        valor={dados.vao_altura}
        aoMudar={mudar("vao_altura")}
      />
      <CampoDeTexto
        rotulo="Vão: largura (m)"
        valor={dados.vao_largura}
        aoMudar={mudar("vao_largura")}
      />
      <label className="flex items-center gap-1.5 text-[11px] text-tinta-2">
        <input
          type="checkbox"
          checked={ativo}
          onChange={(e) => definirAtivo(e.target.checked)}
        />
        Pórtico ativo
      </label>
      <div className="flex items-center gap-2">
        <Botao papel="primaria" onClick={() => void gravar()}>
          Gravar pórtico
        </Botao>
        {erro && <span className="text-[11px] text-vermelho">{erro}</span>}
      </div>
    </div>
  );
}

// --------------------------------------------------------------------------- operadores

function Operadores({
  cadastro,
  aoGravar,
}: {
  cadastro: CadastroDaConcessao;
  aoGravar: () => Promise<void>;
}) {
  const [criando, definirCriando] = useState(false);
  const [erro, definirErro] = useState<string | null>(null);

  async function alternar(o: OperadorDoCadastro) {
    try {
      await api(
        `${BASE}/cadastro/${cadastro.concessao_id}/operadores/${o.id}`,
        {
          method: "PATCH",
          corpo: { ativo: !o.ativo },
        },
      );
      await aoGravar();
    } catch (falha) {
      definirErro(mensagem(falha));
    }
  }

  return (
    <Cartao
      titulo={`Operadores de pagamento (${cadastro.operadores.length})`}
      icone={<Icone.Dinheiro />}
    >
      <ul aria-label="Operadores de pagamento" className="grid gap-1.5">
        {cadastro.operadores.map((o) => (
          <li
            key={o.id}
            className="flex flex-wrap items-center gap-2 rounded-[7px] border border-linha bg-painel-2 px-2.5 py-2"
          >
            <span className="min-w-0 flex-1">
              <b className="text-tinta">{o.razao_social}</b>
              <small className="block text-[11px] text-tinta-3">
                {o.descricao} · {MEIOS[o.meio] ?? o.meio} ·{" "}
                {TIPOS[o.tipo] ?? o.tipo}
              </small>
              <small className="mono block text-[10.5px] text-tinta-3">
                {o.id}
              </small>
            </span>
            {o.ativo && o.pendencias.length > 0 && (
              <Badge cor="amarelo">{o.pendencias.length} pendência(s)</Badge>
            )}
            <Badge cor={o.ativo ? "verde" : "cinza"}>
              {o.ativo ? "ativo" : "desativado"}
            </Badge>
            <Botao onClick={() => void alternar(o)}>
              {o.ativo ? "Desativar" : "Reativar"}
            </Botao>
          </li>
        ))}
      </ul>
      {erro && <p className="text-[11px] text-vermelho">{erro}</p>}
      {criando ? (
        <NovoOperador
          concessaoId={cadastro.concessao_id}
          aoCriar={async () => {
            definirCriando(false);
            await aoGravar();
          }}
          aoCancelar={() => definirCriando(false)}
        />
      ) : (
        <Botao onClick={() => definirCriando(true)}>Novo operador</Botao>
      )}
      <p className="text-[10.5px] text-tinta-3">
        O id do operador vai nos eventos de pago, e o CNPJ não muda depois de
        cadastrado: operador que deixou de valer é desativado, não apagado.
      </p>
    </Cartao>
  );
}

function NovoOperador({
  concessaoId,
  aoCriar,
  aoCancelar,
}: {
  concessaoId: string;
  aoCriar: () => Promise<void>;
  aoCancelar: () => void;
}) {
  const [dados, definirDados] = useState({
    cnpj: "",
    razao_social: "",
    descricao: "",
    localizacao: "",
    municipio_tom: "",
    url: "",
  });
  const [meio, definirMeio] = useState(2);
  const [tipo, definirTipo] = useState(1);
  const [erro, definirErro] = useState<string | null>(null);
  const mudar = (chave: keyof typeof dados) => (v: string) =>
    definirDados((d) => ({ ...d, [chave]: v }));

  async function criar() {
    try {
      await api(`${BASE}/cadastro/${concessaoId}/operadores`, {
        method: "POST",
        corpo: {
          ...dados,
          url: dados.url.trim() || null,
          localizacao: dados.localizacao.trim() || null,
          municipio_tom: dados.municipio_tom.trim()
            ? Number(dados.municipio_tom)
            : null,
          meio,
          tipo,
        },
      });
      await aoCriar();
    } catch (falha) {
      definirErro(mensagem(falha));
    }
  }

  return (
    <div className="grid gap-2 rounded-cartao border border-linha bg-painel-2 p-2.5 [grid-template-columns:repeat(auto-fit,minmax(160px,1fr))]">
      <CampoDeTexto
        rotulo="CNPJ do operador"
        valor={dados.cnpj}
        aoMudar={mudar("cnpj")}
      />
      <CampoDeTexto
        rotulo="Razão social"
        valor={dados.razao_social}
        aoMudar={mudar("razao_social")}
      />
      <CampoDeTexto
        rotulo="Descrição (aparece no app da CNH)"
        valor={dados.descricao}
        aoMudar={mudar("descricao")}
      />
      <CampoDeTexto
        rotulo="Localização"
        valor={dados.localizacao}
        aoMudar={mudar("localizacao")}
      />
      <CampoDeTexto
        rotulo="Município (código TOM)"
        valor={dados.municipio_tom}
        aoMudar={mudar("municipio_tom")}
      />
      <CampoDeTexto
        rotulo="URL (meios digitais)"
        valor={dados.url}
        aoMudar={mudar("url")}
      />
      <label className="grid gap-1 text-[11px] text-tinta-3">
        Meio de pagamento
        <select
          value={meio}
          aria-label="Meio de pagamento"
          onChange={(e) => definirMeio(Number(e.target.value))}
          className={ENTRADA}
        >
          {Object.entries(MEIOS).map(([k, v]) => (
            <option key={k} value={k}>
              {v}
            </option>
          ))}
        </select>
      </label>
      <label className="grid gap-1 text-[11px] text-tinta-3">
        Tipo de cobrança
        <select
          value={tipo}
          aria-label="Tipo de cobrança"
          onChange={(e) => definirTipo(Number(e.target.value))}
          className={ENTRADA}
        >
          {Object.entries(TIPOS).map(([k, v]) => (
            <option key={k} value={k}>
              {v}
            </option>
          ))}
        </select>
      </label>
      <div className="flex items-end gap-1.5">
        <Botao
          papel="primaria"
          disabled={
            !dados.cnpj.trim() ||
            !dados.razao_social.trim() ||
            !dados.descricao.trim()
          }
          onClick={() => void criar()}
        >
          Cadastrar
        </Botao>
        <Botao onClick={aoCancelar}>Cancelar</Botao>
      </div>
      {erro && <p className="text-[11px] text-vermelho">{erro}</p>}
    </div>
  );
}

// --------------------------------------------------------------------------- carga

function mesAtual(): string {
  const hoje = agoraDoInquilino();
  return `${hoje.getFullYear()}-${String(hoje.getMonth() + 1).padStart(2, "0")}`;
}

const ROTULO_DO_STATUS: Record<string, string> = {
  EM_PROCESSAMENTO: "Em processamento",
  PENDENTE_PAGAMENTO: "Pendente de pagamento",
  PAGO: "Pago",
  ISENTO: "Isento",
  CANCELADO: "Cancelado",
};

function CargaHistorica({ cadastro }: { cadastro: CadastroDaConcessao }) {
  const [periodo, definirPeriodo] = useState(mesAtual());
  const [situacao, definirSituacao] =
    useState<PreviaDaCarga["situacao"]>("todos");
  const [previa, definirPrevia] = useState<PreviaDaCarga | null>(null);
  const [erro, definirErro] = useState<string | null>(null);
  const [consultando, definirConsultando] = useState(false);

  // Mudou o que se pede, a prévia antiga deixa de valer.
  useEffect(() => {
    definirPrevia(null);
    definirErro(null);
  }, [periodo, situacao, cadastro.concessao_id]);

  const parametros = useMemo(
    () =>
      new URLSearchParams({
        concessao_id: cadastro.concessao_id,
        periodo: periodo.trim(),
        situacao,
      }).toString(),
    [cadastro.concessao_id, periodo, situacao],
  );

  async function verPrevia() {
    definirConsultando(true);
    try {
      definirPrevia(
        await api<PreviaDaCarga>(`${BASE}/carga/previa?${parametros}`),
      );
      definirErro(null);
    } catch (falha) {
      definirErro(mensagem(falha));
    } finally {
      definirConsultando(false);
    }
  }

  return (
    <Painel
      titulo="Carga histórica"
      rodape={
        <span>
          Um arquivo por período, no formato do contrato: sem cabeçalho,
          separado por ponto e vírgula, texto entre aspas simples. Só entra o
          que foi (ou deveria ter ido) à SENATRAN; placa fictícia não entra fora
          da demonstração.
        </span>
      }
    >
      <div className="flex flex-wrap items-end gap-2">
        <label className="grid gap-1 text-[11px] text-tinta-3">
          Período
          <input
            value={periodo}
            aria-label="Período"
            placeholder="2026, 2026S1 ou 2026-09"
            onChange={(e) => definirPeriodo(e.target.value)}
            className={`${ENTRADA} w-40`}
          />
        </label>
        <label className="grid gap-1 text-[11px] text-tinta-3">
          Situação
          <select
            value={situacao}
            aria-label="Situação"
            onChange={(e) =>
              definirSituacao(e.target.value as PreviaDaCarga["situacao"])
            }
            className={`${ENTRADA} w-36`}
          >
            <option value="todos">Todos</option>
            <option value="pago">Pagos</option>
            <option value="naopago">Não pagos</option>
          </select>
        </label>
        <Botao
          disabled={consultando || !periodo.trim()}
          onClick={() => void verPrevia()}
        >
          <Icone.Busca />
          {consultando ? "Contando…" : "Ver prévia"}
        </Botao>
      </div>
      {erro && <p className="text-[11px] text-vermelho">{erro}</p>}
      {previa && (
        <div className="grid gap-2 rounded-cartao border border-linha bg-painel-2 p-2.5">
          <p className="mono text-[11.5px] text-tinta">{previa.arquivo}</p>
          {previa.demonstracao && (
            <p className="text-[10.5px] text-amarelo">
              Arquivo de demonstração: placas e CNPJ fictícios. Serve para
              conferir o formato, não para enviar.
            </p>
          )}
          <p className="text-[11.5px] text-tinta-2">
            <b>{numeroLongo(previa.linhas)}</b> linha(s)
            {Object.entries(previa.por_status).map(([s, n]) => (
              <span key={s}>
                {" "}
                · {ROTULO_DO_STATUS[s] ?? s}: {numeroLongo(n)}
              </span>
            ))}
          </p>
          {previa.fora_por_falta_de_dado > 0 && (
            <p className="text-[11px] text-amarelo">
              {numeroLongo(previa.fora_por_falta_de_dado)} ficaram de fora por
              falta de placa, categoria ou tarifa — o contrato não os aceita.
            </p>
          )}
          {previa.linhas > 0 && (
            <a
              href={`${BASE}/carga/arquivo?${parametros}`}
              download={previa.arquivo}
              className="inline-flex min-h-8 w-fit items-center gap-1.5 rounded-botao border border-azul-cheio bg-azul-cheio px-2.5 text-[11.5px] font-semibold text-white"
            >
              <Icone.Baixar />
              Baixar arquivo
            </a>
          )}
        </div>
      )}
      <AtualizacaoPorId concessaoId={cadastro.concessao_id} />
    </Painel>
  );
}

function AtualizacaoPorId({ concessaoId }: { concessaoId: string }) {
  const [texto, definirTexto] = useState("");
  const [aviso, definirAviso] = useState<string | null>(null);
  const ids = lerIds(texto);

  async function gerar() {
    try {
      const arquivo = await api<string>(`${BASE}/carga/atualizacao-por-id`, {
        method: "POST",
        corpo: { concessao_id: concessaoId, ids },
        texto: true,
      });
      const linhas = arquivo.split("\n").filter(Boolean).length;
      const blob = new Blob([arquivo], { type: "text/csv;charset=utf-8" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = "atualizacao_por_id.csv";
      a.click();
      URL.revokeObjectURL(a.href);
      definirAviso(
        `${linhas} id(s) no arquivo` +
          (ids.length > linhas
            ? `; ${ids.length - linhas} ficaram de fora (não são desta concessão ou nunca foram enviados).`
            : "."),
      );
    } catch (falha) {
      definirAviso(mensagem(falha));
    }
  }

  return (
    <details className="rounded-cartao border border-linha bg-painel-2 p-2.5">
      <summary className="cursor-pointer text-[11.5px] font-semibold text-tinta-2">
        Atualização por ID
      </summary>
      <p className="mt-1 text-[10.5px] text-tinta-3">
        Para passagens já entregues que precisam voltar a ser atualizadas pela
        API: um id por linha. Cole os ids dos trânsitos; só os desta concessão
        que já foram enviados entram.
      </p>
      <textarea
        value={texto}
        aria-label="Ids dos trânsitos"
        onChange={(e) => definirTexto(e.target.value)}
        rows={4}
        className="mono mt-1.5 w-full rounded-botao border border-linha bg-painel p-2 text-[11px] text-tinta"
      />
      <div className="mt-1.5 flex flex-wrap items-center gap-2">
        <Botao disabled={ids.length === 0} onClick={() => void gerar()}>
          <Icone.Baixar />
          Gerar arquivo ({ids.length})
        </Botao>
        {aviso && <span className="text-[11px] text-tinta-2">{aviso}</span>}
      </div>
    </details>
  );
}
